import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { HiArrowRight } from "react-icons/hi";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslateItems } from "../../hooks/useTranslate";

// ─── Teks UI statis ────────────────────────────────────────────────────────────
const UI_TEXT = {
  EN: {
    // Hero
    heroLabel: "Program",
    heroTitle: "Towards Sustainable and scalable social impact",

    // ProgramGrid
    gridLabel:  "Portfolio",
    gridTitle:  "Rumoh Pangan Aceh Programs",
    viewAll:    "View All",
    learnMore:  "Learn More",
  },

  ID: {
    heroLabel: "Program",
    heroTitle: "Menuju dampak sosial yang berkelanjutan dan terukur.",

    gridLabel:  "Portofolio",
    gridTitle:  "Program Rumoh Pangan Aceh",
    viewAll:    "Lihat Semua",
    learnMore:  "Pelajari Lebih",
  },
};

// ─── useInView ─────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.12) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

// ─── Hero ──────────────────────────────────────────────────────────────────────
const Hero = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full overflow-hidden">
      <div
        className="absolute left-0 top-0 h-full w-1 bg-[#c9862a] z-20"
        style={{ transform: loaded ? "scaleY(1)" : "scaleY(0)", transformOrigin: "top", transition: "transform 1s cubic-bezier(0.16,1,0.3,1) 0.2s" }}
      />
      <div className="grid grid-cols-4 grid-rows-2 h-[340px] md:h-[420px]">
        {[
          "https://i.ibb.co.com/VW6N2JDc/IMG-4567.jpg",
          "https://i.ibb.co.com/DPTHdVs2/gambar.jpg",
          "https://i.ibb.co.com/bRMTtTXd/Nutrition-education-and-food-donation-for-breastfeeding-and-pregnant-women.jpg",
          "https://i.ibb.co.com/zhN2KP84/IMG-0069.jpg",
          "https://i.ibb.co.com/ccxMsrBs/Vegetable-donation-buy-or-donate-program-to-the-Limpok-Village.jpg",
          "https://i.ibb.co.com/jPsSsKDZ/Donasi-sayur.jpg",
          "https://i.ibb.co.com/SC83WSH/Copy-of-PXL-20260411-022836673.jpg",
          "https://i.ibb.co.com/1G1YC605/IMG-2369.jpg"
        ].map((src, i) => (
          <div key={i} className="overflow-hidden">
            <img src={src} alt="" className="w-full h-full object-cover brightness-[0.6] hover:brightness-75 transition-all duration-700" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d0c0a]/80 via-[#1a1a18]/55 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-end pb-14 px-8 md:px-16 lg:px-24">
        <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(24px)", transition: "all 0.9s ease 0.3s" }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#c9862a]" />
            <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">{tx.heroLabel}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-[1.0] max-w-4xl">
            {tx.heroTitle}
          </h1>
        </div>
      </div>
    </section>
  );
};

// ─── ProgramGrid ───────────────────────────────────────────────────────────────

const ProgramGrid = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const ref = useRef(null);
  const inView = useInView(ref);
  const navigate = useNavigate();

  const [rawPrograms, setRawPrograms] = useState([]);

  const toSlug = (text) =>
  text
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // hilangkan diakritik
    .replace(/[^a-z0-9\s-]/g, "")  // hapus karakter selain huruf/angka/spasi/dash
    .trim()
    .replace(/\s+/g, "-");

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const apiUrl = process.env.REACT_APP_URL_API;
        const res = await axios.get(`${apiUrl}/all-program`);
        const data = Array.isArray(res.data) ? res.data : [];
        setRawPrograms(data.slice(0, 6));
      } catch (err) {
        console.error("Error fetching programs:", err);
      }
    };
    fetchPrograms();
  }, []);

  const { translated: programs, loading } = useTranslateItems(
    rawPrograms,
    language,
    ["deskripsi"]
  );

  return (
    <section ref={ref} className="bg-white py-6 pb-16 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="bg-[#1a4a35] px-8 md:px-16 lg:px-20 py-14 relative overflow-hidden">
          <div className="absolute bottom-0 right-0 font-serif text-[180px] font-bold leading-none select-none pointer-events-none opacity-[0.04] text-white">
            PROGRAM
          </div>

          {/* Header */}
          <div
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4"
            style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s ease" }}
          >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-10 bg-[#c9862a]" />
                <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">{tx.gridLabel}</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
                {tx.gridTitle}
              </h2>
            </div>
            <button
              onClick={() => navigate("/portfolio")}
              className="flex items-center gap-2 text-[#c9862a] font-bold text-sm border-b border-[#c9862a]/40 pb-0.5 hover:border-[#c9862a] transition-colors group/link w-fit"
            >
              {tx.viewAll}
              <HiArrowRight className="transition-transform duration-300 group-hover/link:translate-x-1" />
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-10">
            <div className="flex-1 h-px bg-white/10" />
            <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Loading skeleton */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {rawPrograms.map((_, i) => (
                <div key={i} className="bg-white rounded-lg overflow-hidden animate-pulse">
                  <div className="h-48 bg-[#f0ece3]" />
                  <div className="p-6 flex flex-col gap-3">
                    <div className="h-3 bg-[#e8e0d0] rounded w-1/2" />
                    <div className="h-4 bg-[#e8e0d0] rounded w-3/4" />
                    <div className="h-3 bg-[#e8e0d0] rounded w-full" />
                    <div className="h-3 bg-[#e8e0d0] rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {programs.map((program, idx) => {
                const hasImage = program.url && program.url !== null;

                return (
                  <div
                    key={program.id}
                    className="bg-white flex flex-col group/card hover:shadow-xl transition-all duration-300 rounded-lg overflow-hidden"
                    style={{
                      opacity: inView ? 1 : 0,
                      transform: inView ? "translateY(0)" : "translateY(32px)",
                      transition: `all 0.65s ease ${0.1 + idx * 0.08}s`,
                    }}
                  >
                    {/* ── Gambar: hanya tampil jika ada URL ── */}
                    {hasImage && (
                      <div className="h-48 bg-[#f7f3ec] flex items-center justify-center p-8 border-b border-[#e8e0d0] relative overflow-hidden">
                        <img
                          src={program.url}
                          alt={program.judul}
                          className="max-h-24 max-w-[160px] object-contain group-hover/card:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-[#c9862a] group-hover/card:w-full transition-all duration-500" />
                      </div>
                    )}

                    {/* Body */}
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-serif text-base font-bold text-[#1a4a35] leading-snug mb-3">
                        {program.judul}
                      </h3>
                      <div className="w-6 h-[2px] bg-[#c9862a]/40 mb-4 group-hover/card:w-12 transition-all duration-300" />

                      {/* ── Deskripsi: dibatasi 3 baris ── */}
                      <p className="text-[#6b6456] text-sm leading-relaxed flex-1 line-clamp-3">
                        {program.deskripsi}
                      </p>

                      <div className="mt-3 pt-2 border-t border-[#e8e0d0]">
                        <button
                          onClick={() => navigate(`/view-program/${toSlug(program.judul)}`)}
                          className="flex items-center gap-2 border border-[#c9862a] text-[#1a1a18] text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#c9862a] hover:text-white transition-all duration-200 group/btn"
                        >
                          {tx.learnMore}
                          <HiArrowRight size={13} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const ProgramPage = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <section id="hero"><Hero /></section>
    <section id="program"><ProgramGrid /></section>
    <section id="footer"><Footer /></section>
  </main>
);

export default ProgramPage;